import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { mergeProgress, progressPayload, sameProgress, toSyncProgress, type SyncProgress } from './progressSync';
import {
  fetchRemoteProgress,
  readStoredIdentity,
  saveStoredIdentity,
  SyncRequestError,
  verifyPersonalCode,
  writeRemoteProgress,
} from './syncClient';
import { useAtlas } from './store';

export type SyncStatus = 'disconnected' | 'idle' | 'syncing' | 'synced' | 'error';

interface SyncContextValue {
  connected: boolean;
  status: SyncStatus;
  error: string | null;
  verifying: boolean;
  lastSyncedAt: string | null;
  dialogOpen: boolean;
  openDialog: () => void;
  closeDialog: () => void;
  verifyCode: (code: string) => Promise<boolean>;
  syncNow: () => Promise<boolean>;
}

const SyncContext = createContext<SyncContextValue | null>(null);

function errorMessage(error: unknown): string {
  if (error instanceof SyncRequestError) return error.message;
  return '同步失败；本地数据未受影响。';
}

export function SyncProvider({ children }: { children: ReactNode }) {
  const atlas = useAtlas();
  const atlasRef = useRef(atlas);
  atlasRef.current = atlas;
  const codeRef = useRef('');
  const lastSerializedRef = useRef('');
  const syncingRef = useRef(false);
  const startupRef = useRef(false);
  const initialSyncDoneRef = useRef(false);

  const [connected, setConnected] = useState(false);
  const [status, setStatus] = useState<SyncStatus>('disconnected');
  const [error, setError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const syncNow = useCallback(async (): Promise<boolean> => {
    const code = codeRef.current;
    if (!code) return false;
    if (syncingRef.current) return false;
    syncingRef.current = true;
    setStatus('syncing');
    setError(null);
    try {
      const remote = await fetchRemoteProgress(code);
      const local = toSyncProgress(atlasRef.current);
      let merged = remote.exists && remote.state ? mergeProgress(local, remote.state) : local;
      if (remote.exists && remote.state && sameProgress(merged, remote.state)) {
        lastSerializedRef.current = progressPayload(merged);
        atlasRef.current.applySyncedProgress(remote.state);
        setStatus('synced');
        setError(null);
        if (remote.updatedAt) setLastSyncedAt(remote.updatedAt);
        return true;
      }
      try {
        const written = await writeRemoteProgress(code, merged, remote.version);
        setLastSyncedAt(written.updatedAt);
      } catch (writeError) {
        if (!(writeError instanceof SyncRequestError) || writeError.status !== 409) throw writeError;
        const conflict = writeError as SyncRequestError & { remote?: { state: SyncProgress | null; version: number } };
        const latest = conflict.remote?.state ? conflict.remote : await fetchRemoteProgress(code);
        merged = mergeProgress(merged, latest.state ?? local);
        const written = await writeRemoteProgress(code, merged, latest.version);
        setLastSyncedAt(written.updatedAt);
      }
      lastSerializedRef.current = progressPayload(merged);
      atlasRef.current.applySyncedProgress(merged);
      setStatus('synced');
      setError(null);
      return true;
    } catch (syncError) {
      setStatus('error');
      setError(errorMessage(syncError));
      return false;
    } finally {
      syncingRef.current = false;
      initialSyncDoneRef.current = true;
    }
  }, []);

  useEffect(() => {
    const identity = readStoredIdentity();
    if (!identity) return;
    codeRef.current = identity.code;
    setConnected(true);
    setStatus('idle');
  }, []);

  useEffect(() => {
    if (!connected || startupRef.current) return;
    startupRef.current = true;
    void syncNow();
  }, [connected, syncNow]);

  const syncPayload = JSON.stringify(toSyncProgress(atlas));
  useEffect(() => {
    if (!connected || !startupRef.current || !initialSyncDoneRef.current) return;
    if (syncPayload === lastSerializedRef.current) return;
    const timer = window.setTimeout(() => {
      const current = progressPayload(toSyncProgress(atlasRef.current));
      if (current === lastSerializedRef.current) return;
      void syncNow();
    }, 1400);
    return () => window.clearTimeout(timer);
  }, [connected, syncPayload, syncNow]);

  useEffect(() => {
    if (!connected) return;
    const refresh = () => {
      if (document.visibilityState === 'visible') void syncNow();
    };
    const onOnline = () => void syncNow();
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('online', onOnline);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('online', onOnline);
    };
  }, [connected, syncNow]);

  const verifyCode = useCallback(async (code: string): Promise<boolean> => {
    setVerifying(true);
    setError(null);
    try {
      await verifyPersonalCode(code);
      const identity = { code, verifiedAt: new Date().toISOString() };
      saveStoredIdentity(identity);
      codeRef.current = code;
      startupRef.current = true;
      setConnected(true);
      setStatus('syncing');
      await syncNow();
      return true;
    } catch (verifyError) {
      setError(errorMessage(verifyError));
      setStatus(connected ? 'error' : 'disconnected');
      return false;
    } finally {
      setVerifying(false);
    }
  }, [connected, syncNow]);

  const openDialog = useCallback(() => {
    setDialogOpen(true);
  }, []);
  const closeDialog = useCallback(() => setDialogOpen(false), []);

  return (
    <SyncContext.Provider
      value={{
        connected,
        status,
        error,
        verifying,
        lastSyncedAt,
        dialogOpen,
        openDialog,
        closeDialog,
        verifyCode,
        syncNow,
      }}
    >
      {children}
    </SyncContext.Provider>
  );
}

export function useSync(): SyncContextValue {
  const context = useContext(SyncContext);
  if (!context) throw new Error('useSync must be used within SyncProvider');
  return context;
}
